import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { docsNavigation } from "../../config/docs";

interface DocsSidebarProps {
  onClose?: () => void;
}

const icons: Record<string, React.ReactNode> = {
  rocket: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  ),
  cube: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
      />
    </svg>
  ),
  book: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  ),
  code: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
      />
    </svg>
  ),
};

export function DocsSidebar({ onClose }: DocsSidebarProps) {
  const { t } = useTranslation();
  const params = useParams();
  const currentCategory = params["*"]?.split("/")[0];
  const currentSlug = params["*"]?.split("/")[1];

  return (
    <nav className="h-full overflow-y-auto p-4 custom-scrollbar">
      <div className="mb-6 px-2">
        <NavLink
          to="/docs"
          className={({ isActive }) =>
            `block text-sm font-medium transition-colors ${
              isActive ? "text-warm-50" : "text-warm-500 hover:text-warm-100"
            }`
          }
          onClick={onClose}
        >
          {t("docs.sidebar.overview")}
        </NavLink>
      </div>

      {docsNavigation.map((category) => (
        <div key={category.id} className="mb-4">
          <NavLink
            to={`/docs/${category.id}/${category.items[0].slug}`}
            className={({ isActive }) =>
              `flex items-center gap-2 px-2 py-2 text-sm font-medium transition-colors ${
                isActive || currentCategory === category.id
                  ? "text-warm-50 bg-dark-hover rounded-lg"
                  : "text-warm-400 hover:text-warm-100"
              }`
            }
            onClick={onClose}
          >
            {icons[category.icon]}
            {t(category.titleKey)}
          </NavLink>

          {(currentCategory === category.id || !currentCategory) && (
            <div className="ml-7 mt-1 space-y-0.5">
              {category.items.map((item) => (
                <NavLink
                  key={item.slug}
                  to={`/docs/${category.id}/${item.slug}`}
                  className={({ isActive }) =>
                    `block px-2 py-1.5 text-sm rounded transition-colors ${
                      isActive || currentSlug === item.slug
                        ? "text-warm-100 bg-dark-hover"
                        : "text-warm-500 hover:text-warm-100 hover:bg-dark-hover"
                    }`
                  }
                  onClick={onClose}
                >
                  {t(item.titleKey)}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}
